import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { Slot } from "radix-ui";
import type { ButtonHTMLAttributes } from "react";

import { cn } from "@/lib/cn";

export const buttonVariants = cva(
  // Press feedback: a slight squeeze while held (not on disabled buttons). Touch screens get 44 px targets.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border font-semibold transition-[background-color,border-color,color,box-shadow,scale,opacity] duration-150 ease-out not-disabled:active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-45 [&_svg]:size-[18px] [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "border-brand bg-brand text-white shadow-[0_1px_2px_rgb(16_24_40/0.12)] hover:bg-brand-700 hover:shadow-[0_2px_6px_rgb(51_100_174/0.28)]",
        secondary: "border-line-strong bg-surface text-ink shadow-[0_1px_2px_rgb(16_24_40/0.05)] hover:border-[#b8c2cf] hover:bg-surface-2",
        ghost: "border-transparent bg-transparent text-ink-2 hover:bg-surface-2",
        danger: "border-transparent bg-transparent text-danger hover:bg-danger-bg",
        // For the confirming button of an action that cannot be undone.
        destructive: "border-danger bg-danger text-white hover:bg-[#912018] hover:shadow-[0_2px_6px_rgb(180_35_24/0.25)]",
      },
      size: {
        sm: "h-8 px-2.5 text-[13px] pointer-coarse:h-11",
        md: "h-10 px-3.5 pointer-coarse:h-11",
        lg: "h-[46px] px-[18px] text-[15px]",
        icon: "size-9 p-0 pointer-coarse:size-11",
      },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Working on it: disabled, with a spinner in place of the icon (the label stays, so does the width). */
  loading?: boolean;
}

export function Button({ className, variant, size, asChild, type, loading, disabled, children, ...props }: ButtonProps) {
  if (asChild) {
    return (
      <Slot.Root className={cn(buttonVariants({ variant, size }), className)} {...props}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      className={cn(buttonVariants({ variant, size }), loading && "cursor-progress disabled:opacity-80 [&>svg:not(.spinner)]:hidden", className)}
      type={type ?? "button"}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <LoaderCircle aria-hidden className="spinner animate-spin" />}
      {children}
    </button>
  );
}
